import { SseParser } from "./sse-parser";

const SNAPSHOT_EVENT = "notifications.snapshot";

export class StreamHttpError extends Error {
  constructor(public readonly status: number) {
    super(`Notification stream returned HTTP ${status}`);
    this.name = "StreamHttpError";
  }
}

export type StreamTransportOptions = {
  mode: "fetch" | "xhr";
  url: string;
  credentials: RequestCredentials;
  signal: AbortSignal;
  getAuthToken?: () => string | null | Promise<string | null>;
  onSnapshot: (value: unknown) => void;
  fetchImpl?: typeof fetch;
  xhrFactory?: () => XMLHttpRequest;
};

export async function consumeNotificationStream(options: StreamTransportOptions): Promise<void> {
  if (options.signal.aborted) throw new DOMException("The operation was aborted.", "AbortError");
  if (options.mode === "xhr") {
    await consumeXhrStream(options);
    return;
  }
  const headers = new Headers({ Accept: "text/event-stream" });
  const token = await options.getAuthToken?.();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await (options.fetchImpl ?? fetch)(options.url, {
    headers,
    credentials: options.credentials,
    signal: options.signal,
  });
  if (response.status === 401) throw new StreamHttpError(401);
  if (!response.ok) throw new StreamHttpError(response.status);
  const reader = response.body?.getReader();
  if (!reader) throw new Error("Notification stream response has no readable body.");
  const parser = new SseParser();
  const decoder = new TextDecoder();
  try {
    while (!options.signal.aborted) {
      const result = await reader.read();
      if (result.done) break;
      for (const message of parser.push(decoder.decode(result.value, { stream: true }))) {
        if (message.event === SNAPSHOT_EVENT) options.onSnapshot(JSON.parse(message.data));
      }
    }
    for (const message of parser.push(decoder.decode())) {
      if (message.event === SNAPSHOT_EVENT) options.onSnapshot(JSON.parse(message.data));
    }
  } finally {
    await reader.cancel().catch(() => undefined);
  }
}

function consumeXhrStream(options: StreamTransportOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const factory = options.xhrFactory ?? (() => new XMLHttpRequest());
    if (typeof XMLHttpRequest === "undefined" && !options.xhrFactory) {
      reject(new Error("XMLHttpRequest is not available."));
      return;
    }
    const xhr = factory();
    const parser = new SseParser();
    let offset = 0;
    let settled = false;
    let aborted = false;
    const settle = (error?: unknown) => {
      if (settled) return;
      settled = true;
      options.signal.removeEventListener("abort", abort);
      if (error) reject(error);
      else resolve();
    };
    const abort = () => {
      aborted = true;
      xhr.abort();
      settle(new DOMException("The operation was aborted.", "AbortError"));
    };
    void (async () => {
      const token = await options.getAuthToken?.();
      if (settled || options.signal.aborted) {
        settle(new DOMException("The operation was aborted.", "AbortError"));
        return;
      }
      xhr.open("GET", options.url);
      xhr.withCredentials = options.credentials === "include";
      xhr.setRequestHeader("Accept", "text/event-stream");
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.onprogress = () => {
        const next = xhr.responseText.slice(offset);
        offset = xhr.responseText.length;
        try {
          for (const message of parser.push(next)) {
            if (message.event === SNAPSHOT_EVENT) options.onSnapshot(JSON.parse(message.data));
          }
        } catch (error) {
          settle(error);
          xhr.abort();
        }
      };
      xhr.onerror = () => settle(new Error("Notification stream failed."));
      xhr.onabort = () => {
        if (!aborted) settle(new DOMException("The operation was aborted.", "AbortError"));
      };
      xhr.onload = () => {
        if (aborted) return;
        if (xhr.status === 401) settle(new StreamHttpError(401));
        else if (xhr.status < 200 || xhr.status >= 300) settle(new StreamHttpError(xhr.status));
        else settle();
      };
      options.signal.addEventListener("abort", abort, { once: true });
      xhr.send();
    })().catch(settle);
  });
}
